import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Row, Col, Button, Container, Badge, Modal, Form } from "react-bootstrap";
import classes from "../WebsiteManagement/websiteManagement.module.css";
import Page from "../pagination/pagination";
import {
  GetAdminTrainBookings,
  DownloadTrainTicketPdfById,
  DownloadTrainInvoicePdfById,
  CompleteTrainCancellation,
} from "../../entities/action/action";

const PAGE_SIZE = 50;
const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const formatStation = (name, code) => {
  const n = String(name || "").trim();
  const c = String(code || "").trim();
  if (n && c) return `${n} (${c})`;
  return n || c || "—";
};

const TrainBookingCard = ({ item, onCompleted }) => {
  const orderDate = new Date(item?.orderDate);
  const travelDate = new Date(`${item?.journeyDate || ""}T12:00:00`);
  const [showModal, setShowModal] = useState(false);
  const [adminRemarks, setAdminRemarks] = useState("");
  const [loading, setLoading] = useState(false);

  const pendingCancellation = String(item?.cancellationStatus || "").toUpperCase() === "PENDING";
  const cancellationSuccess = String(item?.cancellationStatus || "").toUpperCase() === "CANCELLATION_SUCCESS";
  const downloadRef = item?.pnr || item?.bookingId || item?.id;

  const completeCancellation = async () => {
    if (!item?.cancellationRequestId) return;
    setLoading(true);
    try {
      const res = await CompleteTrainCancellation(item.cancellationRequestId, adminRemarks);
      if (res?.success) {
        setShowModal(false);
        setAdminRemarks("");
        if (onCompleted) {
          onCompleted();
        }
      } else {
        alert(res?.message || "Failed to update cancellation status.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Row
        style={{
          backgroundColor: pendingCancellation ? "#fff8e1" : "#f9f9f9",
          padding: "15px 25px",
          alignItems: "center",
          boxShadow: "-1px 2px 8px #ddd",
          margin: "15px 0",
          border: pendingCancellation ? "1px solid #ffc107" : "none",
        }}
      >
        <Col md={2}>
          <div style={{ color: "#adb0b8" }}>{Number.isNaN(travelDate.getTime()) ? "—" : travelDate.getDate()}</div>
          <div>{Number.isNaN(travelDate.getTime()) ? "" : month[travelDate.getMonth()]}</div>
          <div style={{ color: "#ef3f3e" }}>{Number.isNaN(travelDate.getTime()) ? "" : travelDate.getFullYear()}</div>
        </Col>
        <Col md={2}>🚆</Col>
        <Col md={3}>
          <div>
            {formatStation(item.boardingStation, item.boardingStationCode)} →{" "}
            {formatStation(item.destinationStation, item.destinationStationCode)}
          </div>
          <div style={{ color: "gray" }}>{item.customerName || "—"}</div>
          <div style={{ color: "gray" }}>{item.customerMobile || "—"}</div>
          <div style={{ color: "gray" }}>
            {[item.trainNumber, item.trainName].filter(Boolean).join(" · ") || "Train"}
          </div>
          <div style={{ color: "gray" }}>
            {(item.passengers || [])
              .map((p) => p.passengerName)
              .filter(Boolean)
              .join(", ") || "—"}
          </div>
          <div style={{ color: "gray" }}>INR {Number(item.amount || 0).toLocaleString("en-IN")}</div>
          {pendingCancellation && (
            <div style={{ marginTop: 8 }}>
              <Badge bg="warning" text="dark">
                Cancellation requested
              </Badge>
              {item.cancellationRemarks && (
                <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                  User remark: {item.cancellationRemarks}
                </div>
              )}
            </div>
          )}
        </Col>
        <Col md={3}>
          <p>PNR: {item?.pnr || "—"}</p>
          {item?.bookingId && <div style={{ color: "gray", fontSize: 13 }}>Ref: {item.bookingId}</div>}
          <div style={{ color: "gray" }}>
            {orderDate.getDate()}/{month[orderDate.getMonth()]}/{orderDate.getFullYear()}
          </div>
        </Col>
        <Col md={2}>
          <Button
            size="sm"
            variant="outline-primary"
            style={{ fontSize: 10, marginBottom: 5 }}
            onClick={() => DownloadTrainTicketPdfById(item.id, downloadRef)}
          >
            Download ticket
          </Button>
          <Button
            size="sm"
            variant="outline-secondary"
            style={{ fontSize: 10, marginBottom: 5 }}
            onClick={() => DownloadTrainInvoicePdfById(item.id, downloadRef)}
          >
            Download invoice
          </Button>
          {pendingCancellation && (
            <Button
              size="sm"
              variant="success"
              style={{ fontSize: 10, marginBottom: 5 }}
              onClick={() => setShowModal(true)}
            >
              Mark cancellation success
            </Button>
          )}
          <div
            style={{
              background: pendingCancellation ? "#ffc107" : cancellationSuccess ? "#198754" : "#0dcaf0",
              color: cancellationSuccess ? "#fff" : "#000",
              padding: 5,
              marginTop: 5,
              textAlign: "center",
              fontSize: 11,
            }}
          >
            {pendingCancellation
              ? "PENDING CANCELLATION"
              : cancellationSuccess
              ? "CANCELLATION SUCCESS"
              : (item?.status || "").toUpperCase()}
          </div>
        </Col>
      </Row>

      <Modal show={showModal} onHide={() => !loading && setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Complete train cancellation</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 14 }}>
            Confirm that you have manually cancelled this train booking with the railway / provider.
          </p>
          {item.cancellationRemarks && (
            <p style={{ fontSize: 13, color: "#666" }}>
              <strong>User remark:</strong> {item.cancellationRemarks}
            </p>
          )}
          <Form.Group>
            <Form.Label>Admin remarks (optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={adminRemarks}
              onChange={(e) => setAdminRemarks(e.target.value)}
              placeholder="Internal note about the cancellation"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={loading}>
            Close
          </Button>
          <Button variant="success" onClick={completeCancellation} disabled={loading}>
            {loading ? "Saving…" : "Mark cancellation success"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const TrainBookings = () => {
  const [all, setAll] = useState([]);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoadError("");
    try {
      const res = await GetAdminTrainBookings();
      if (res?.success && Array.isArray(res.bookings)) {
        setAll(res.bookings);
      } else {
        setAll([]);
        setLoadError(res?.message || "Failed to load train bookings.");
      }
    } catch {
      setAll([]);
      setLoadError("Failed to load train bookings.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, pendingOnly]);

  const filtered = useMemo(() => {
    let rows = all;
    if (pendingOnly) {
      rows = rows.filter((b) => String(b.cancellationStatus || "").toUpperCase() === "PENDING");
    }
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((b) => {
      const name = String(b.customerName || "").toLowerCase();
      const phone = String(b.customerMobile || "").toLowerCase();
      const pnr = String(b.pnr || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || pnr.includes(q);
    });
  }, [all, search, pendingOnly]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <Fragment>
      <Container style={{ display: "flex", justifyContent: "center", margin: "20px 0", gap: 12, flexWrap: "wrap" }}>
        <form
          onSubmit={(e) => e.preventDefault()}
          style={{ display: "flex", alignItems: "stretch", width: "50vw", minWidth: 280 }}
        >
          <input
            className={classes.SearchInput}
            placeholder="Search by name, phone, or PNR"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              setSearch(e.target.value);
            }}
          />
          <button type="button" className={classes.SearchButton} onClick={() => setSearch(searchInput)}>
            Search
          </button>
        </form>
        <Button
          variant={pendingOnly ? "warning" : "outline-warning"}
          size="sm"
          onClick={() => setPendingOnly((v) => !v)}
        >
          {pendingOnly ? "Showing pending cancellations" : "Show pending cancellations only"}
        </Button>
      </Container>
      {loadError && (
        <p style={{ color: "#b91c1c", textAlign: "center", margin: "12px 0" }}>{loadError}</p>
      )}
      {pageItems.length ? (
        pageItems.map((item) => <TrainBookingCard key={item.id} item={item} onCompleted={load} />)
      ) : (
        !loadError && <p>No train bookings found</p>
      )}
      {totalPages > 1 && <Page count={totalPages} page={page} change={(_, v) => setPage(v)} />}
    </Fragment>
  );
};

export default TrainBookings;
